/**
 * Sections tarifaires prêtes à poser dans les hubs — un seul tag à rendre.
 *
 * Regroupe ici les titres/chapôs FR/EN/AR pour que les vues de hub restent des
 * assemblages, et que la copie éditoriale vive à côté des données qu'elle
 * commente. Les montants, eux, ne sont jamais écrits : ils viennent des mêmes
 * registres que le moteur de devis et que les fiches produit.
 */
import type { Locale } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";
import { PricingSection } from "./PricingSection";
import { TransferRatesTable } from "./TransferRatesTable";
import { DisposalPricingTable } from "./DisposalPricingTable";
import { RentalRatesTable } from "./RentalRatesTable";

type SectionCopy = { eyebrow: string; heading: string; intro: string };

const TRANSFER_COPY: I18n<SectionCopy> = {
  fr: {
    eyebrow: "Tarifs transferts",
    heading: "Le prix de chaque liaison, par classe de véhicule",
    intro:
      "Nos forfaits sont fixés avant le départ : le montant annoncé est celui que vous payez, quel que soit le trafic. Les prix ci-dessous valent dans les deux sens et incluent taxes et péages.",
  },
  en: {
    eyebrow: "Transfer rates",
    heading: "The price of every route, by vehicle class",
    intro:
      "Our prices are fixed before departure: the amount quoted is the amount you pay, whatever the traffic. The rates below apply in both directions and include taxes and tolls.",
  },
  ar: {
    eyebrow: "أسعار النقل",
    heading: "سعر كل وجهة، حسب فئة السيارة",
    intro:
      "أسعارنا محدَّدة قبل الانطلاق: المبلغ المعلن هو المبلغ الذي تدفعونه، مهما كانت حركة المرور. الأسعار أدناه صالحة في الاتجاهين وتشمل الضرائب ورسوم الطرق.",
  },
};

const DISPOSAL_COPY: I18n<SectionCopy> = {
  fr: {
    eyebrow: "Mise à disposition",
    heading: "Un chauffeur et un véhicule à vous, à l'heure ou à la journée",
    intro:
      "Quand la journée ne se résume pas à un aller simple — rendez-vous enchaînés, shopping, repérages, soirée — la mise à disposition réserve un chauffeur et son véhicule pour une durée définie, sans recalculer un prix à chaque étape.",
  },
  en: {
    eyebrow: "Chauffeur hire",
    heading: "A chauffeur and a car of your own, by the hour or by the day",
    intro:
      "When the day is more than a single ride — back-to-back meetings, shopping, location scouting, an evening out — hourly hire keeps a chauffeur and their car with you for a set duration, with no new price at every stop.",
  },
  ar: {
    eyebrow: "سيارة تحت التصرّف",
    heading: "سائق وسيارة تحت تصرّفكم، بالساعة أو باليوم",
    intro:
      "حين لا يكون اليوم مجرّد رحلة واحدة — مواعيد متتالية، تسوّق، جولات، سهرة — يضع نظام «تحت التصرّف» سائقًا وسيارته رهن إشارتكم لمدة محدّدة، دون إعادة حساب السعر عند كل محطة.",
  },
};

const RENTAL_COPY: I18n<SectionCopy> = {
  fr: {
    eyebrow: "Tarifs location",
    heading: "Nos prix à la journée, affichés",
    intro:
      "Les tarifs baissent avec la durée. Chaque ligne mène à la fiche du véhicule : photos, caractéristiques et conditions détaillées.",
  },
  en: {
    eyebrow: "Rental rates",
    heading: "Our daily prices, in the open",
    intro:
      "Rates go down as the rental gets longer. Each row leads to the vehicle page: photos, specifications and detailed terms.",
  },
  ar: {
    eyebrow: "أسعار التأجير",
    heading: "أسعارنا اليومية، معلنة",
    intro:
      "تنخفض الأسعار كلما طالت مدة التأجير. كل سطر يقود إلى صفحة السيارة: الصور والمواصفات والشروط التفصيلية.",
  },
};

export function TransferRatesSection({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const copy = tr(TRANSFER_COPY, locale);
  return (
    <PricingSection {...copy} anchorId="tarifs" surface="tinted">
      <TransferRatesTable locale={locale} />
    </PricingSection>
  );
}

export function DisposalPricingSection({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const copy = tr(DISPOSAL_COPY, locale);
  return (
    <PricingSection {...copy} anchorId="mise-a-disposition">
      <DisposalPricingTable locale={locale} />
    </PricingSection>
  );
}

export function RentalRatesSection({
  locale = "fr",
}: {
  locale?: Locale;
}): JSX.Element {
  const copy = tr(RENTAL_COPY, locale);
  return (
    <PricingSection {...copy} anchorId="tarifs" surface="tinted">
      <RentalRatesTable locale={locale} />
    </PricingSection>
  );
}
